import React, { type CSSProperties, forwardRef } from 'react';

import Portal from '@atlaskit/portal';
import { Transition as ReactTransitionGroup } from 'react-transition-group';
import { Transition } from './Transition';
import type { SpotlightContainerProps } from './types';

export const enterDuration = 200;
export const exitDuration = 120;

const defaultTransitionStyles: CSSProperties = {
	opacity: 0,
	transitionProperty: 'opacity',
	transitionTimingFunction: 'ease-in-out',
	transitionDuration: `${enterDuration}ms`,
};

const statefulTransitionStyles: { [key: string]: CSSProperties } = {
	entering: {},
	entered: { opacity: 1 },
	exiting: { transitionDuration: `${exitDuration}ms`, opacity: 0 },
	exited: {},
	unmounted: {},
};

const timeoutDurations = { enter: 0, exit: exitDuration };

export const SpotlightContainer: React.ForwardRefExoticComponent<
	SpotlightContainerProps & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, SpotlightContainerProps>(
	(
		{
			containerStyles,
			showSpotlight,
			shouldBlink,
			zIndex,
			stopBlink,
			ariaLabelledBy,
			shouldUseReactTransitionGroup = true,
			...rest
		},
		ref,
	) => {

		return (
			<>
				{shouldUseReactTransitionGroup ? (
					<ReactTransitionGroup
						appear={true}
						in={showSpotlight && !shouldBlink}
						timeout={timeoutDurations}
						onExited={stopBlink}
						unmountOnExit={!shouldBlink}
					>
						{(state) => (
							<Portal zIndex={zIndex}>
								<div
									style={{
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										...containerStyles,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										...defaultTransitionStyles,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										...statefulTransitionStyles[state],
									}}
									ref={ref}
									aria-labelledby={ariaLabelledBy}
									{...rest}
								/>
							</Portal>
						)}
					</ReactTransitionGroup>
				) : (
					<Transition
						containerStyles={containerStyles}
						ref={ref}
						ariaLabelledBy={ariaLabelledBy}
						stopBlink={stopBlink}
						shouldBlink={shouldBlink}
						showSpotlight={showSpotlight}
						zIndex={zIndex}
						{...rest}
					/>
				)}
			</>
		);
	},
);
